import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { UsersService } from '@brafton/atlas-api-client';
import { OwnerStub, UserProfile } from '@brafton/common-framework-library';
import { Observable } from 'rxjs';
import { first, map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'shared-users-dropdown',
  templateUrl: './users-dropdown.component.html',
  styleUrls: ['./users-dropdown.component.scss'],
  host: { class: 'users-dropdown' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SharedUsersDropdownComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SharedUsersDropdownComponent implements OnInit, ControlValueAccessor {
  @Input() userTypes: string[] = [];
  @Input() placeholder?: string;
  @Output() selectedUser = new EventEmitter<UserProfile | null>();
  @Input() disabled!: boolean;
  @Input() showProfileImage = true;
  @Input() resetOnSelected = false;
  @ViewChild('profileInput') profileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

  value!: UserProfile | OwnerStub | null;
  orgUsers!: UserProfile[];
  typedInput!: string;
  touched = false;
  myControl = new UntypedFormControl();

  filteredUsers!: Observable<UserProfile[]>;

  constructor(private userService: UsersService) {}

  onChange = (user: any) => {};
  onTouched = () => {};

  ngOnInit() {
    // two different source data sets - users with permissions, or just basic users:
    if (this.userTypes.length > 0) {
      this.userService
        .listUsersWithPermissions(705, true)
        .pipe(first())
        .subscribe((r) => {
          // filter users to those matching the specified user types/permission type
          this.orgUsers = r.filter(
            (u: any) =>
              u.permission &&
              this.userTypes.map((ut) => ut.toLocaleLowerCase()).includes(u.permission.name.toLocaleLowerCase()),
          );
          // now we have users - kick off the auto complete filter
          this.filteredUsers = this.myControl.valueChanges.pipe(
            startWith(''),
            // we're just gragging the typed input
            tap((input) => (input !== '' ? (this.typedInput = input) : '')),
            // filter the list of userprofiles based on input string
            map((value) => this.filterUsers(value)),
            // reset the output selected user as we're filtering on something now
            tap((results) => this.resetOutputUser()),
          );
        });
    } else {
      this.userService
        .listUsers(705, true)
        .pipe(first())
        .subscribe((r) => {
          this.orgUsers = r;
          // now we have users - kick off the auto complete filter
          this.filteredUsers = this.myControl.valueChanges.pipe(
            startWith(''),
            // we're just gragging the typed input
            tap((input) => (input !== '' ? (this.typedInput = input) : '')),
            // filter the list of userprofiles based on input string
            map((value) => this.filterUsers(value)),
            // reset the output selected user as we're filtering on something now
            tap((results) => this.resetOutputUser()),
          );
        });
    }
  }

  getInputBoxValue(): string {
    return (this.value as OwnerStub)?.name || (this.value as UserProfile)?.displayName || this.typedInput;
  }

  selectUser(autoCompleteUser: UserProfile) {
    this.value = autoCompleteUser;
    this.onChange(this.value);
    this.selectedUser.emit(autoCompleteUser);

    if (this.resetOnSelected === true) {
      this.profileInput.nativeElement.value = '';
      this.myControl.setValue(null);
      this.autocomplete.closePanel();
      this.value = null;
    }
  }

  resetOutputUser() {
    if (this.value) {
      this.value = null;
      this.onChange(null);
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  inputTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  private filterUsers(value: string): UserProfile[] {
    if (!value) {
      return this.orgUsers;
    }

    return this.orgUsers?.filter((user) => user.displayName.toLowerCase().includes(value.toLowerCase()));
  }
}
